// Cosmetic items: pure vanity, zero gameplay effect. Bought once with coins, then equipped
// per athlete (one item per slot). Auras/trails are whole-body overlays (work on any athlete
// with no art); HEAD items swap the whole sprite for a Higgsfield variant baked with the
// accessory (`<skin>_<accessory>.png`) — perfect integration, no anchors.

export type CosmeticSlot = 'head' | 'aura' | 'trail';

export interface Cosmetic {
  id: string;
  slot: CosmeticSlot;
  /** i18n key for the display name. */
  nameKey: string;
  price: number;
  /** Primary colour (aura glow / swatch). */
  color: number;
  /** Head slot: the baked sprite-variant suffix, used as `<skin>_<accessory>`. */
  accessory?: string;
  /** Particle tints (trail slot only). */
  tints?: number[];
}

export const SLOTS: CosmeticSlot[] = ['head', 'aura', 'trail'];

export const COSMETICS: Cosmetic[] = [
  // ── Head (full-sprite variants baked with Higgsfield) ──
  { id: 'head_shades', slot: 'head', nameKey: 'cos.shades', price: 250, color: 0x2a66d0, accessory: 'shades' },
  { id: 'head_cap', slot: 'head', nameKey: 'cos.cap', price: 360, color: 0xff5d6c, accessory: 'cap' },
  { id: 'head_crown', slot: 'head', nameKey: 'cos.crown', price: 650, color: 0xffd24a, accessory: 'crown' },

  // ── Aura (glow behind the athlete) ──
  { id: 'aura_gold', slot: 'aura', nameKey: 'cos.auraGold', price: 300, color: 0xffd24a },
  { id: 'aura_neon', slot: 'aura', nameKey: 'cos.auraNeon', price: 350, color: 0x59d2ff },
  { id: 'aura_fire', slot: 'aura', nameKey: 'cos.auraFire', price: 400, color: 0xff7a59 },
  { id: 'aura_ice', slot: 'aura', nameKey: 'cos.auraIce', price: 400, color: 0x9fe6ff },
  { id: 'aura_toxic', slot: 'aura', nameKey: 'cos.auraToxic', price: 400, color: 0x6ef0a0 },
  { id: 'aura_royal', slot: 'aura', nameKey: 'cos.auraRoyal', price: 480, color: 0xb083e6 },

  // ── Trail (particles while moving) ──
  { id: 'trail_spark', slot: 'trail', nameKey: 'cos.trailSpark', price: 350, color: 0xffd24a, tints: [0xffd24a, 0xfff4c2] },
  { id: 'trail_fire', slot: 'trail', nameKey: 'cos.trailFire', price: 450, color: 0xff7a59, tints: [0xff7a59, 0xffd24a, 0xff3b3b] },
  { id: 'trail_bubbles', slot: 'trail', nameKey: 'cos.trailBubbles', price: 400, color: 0x9fe6ff, tints: [0x9fe6ff, 0xffffff] },
  { id: 'trail_rainbow', slot: 'trail', nameKey: 'cos.trailRainbow', price: 520, color: 0xff7a59, tints: [0xff5d6c, 0xffd24a, 0x4ade80, 0x59d2ff, 0xb083e6] },
];

export function getCosmetic(id: string): Cosmetic | undefined {
  return COSMETICS.find((c) => c.id === id);
}

export function cosmeticsBySlot(slot: CosmeticSlot): Cosmetic[] {
  return COSMETICS.filter((c) => c.slot === slot);
}

/** Build the baked-variant texture key/url for a head accessory on a given skin. */
export function variantKey(skinId: string, accessory: string): string {
  return `${skinId}_${accessory}`;
}
export function variantUrl(skinId: string, accessory: string): string {
  return `assets/sprites/full/${skinId}_${accessory}.png`;
}
